<template>
    <div
        ref="wrapper"
        class="l-ss-c-offers-wrapper"
        :class="{
            'l-ss-c-offers-wrapper-grid': isGridView,
            'l-ss-c-offers-wrapper-grouped': grouped
        }"
        :style="gridStyles"
        @scroll="processAvailableArrows"
        @click="globalHandleOfferClick"
        @touchstart="onTouchStart($event)"
        @touchend="onTouchEnd($event)"
        @touchmove="onTouchEnd($event)">
        <offer
            v-for="(offer, i) in offers"
            :key="`offer-${offer.id}`"
            ref="offer"
            :offer="offer"
            :settings="$configuration.offer"
            :layout="offerLayout"
            :mobile="isMobile"
            :link-target-blank="$configuration.linkTargetBlank"
            :is-cart-count-available="$configuration.cartCount.offer.count"
            :offer-index="i"
            v-bind="offerProps"
            @click="handleOfferClick($event, offer)"
            @add-to-cart-click="addToCard($event)"
            @remove-from-cart-click="removeFromCard($event)"
            @add-to-favourites-click="addToFavourites($event)"
            @remove-from-favourites-click="removeFromFavourites($event)"
            @load-grouped-offers="loadGroupedOffers"/>

        <div
            class="l-ss-c-fake-offer"
            v-for="fakeOffer in fakeOffers"
            :key="fakeOffer"></div>

        <slot name="append"></slot>

        <template v-if="!isMobile && isGridView && grouped && gridColumnCount < offers.length">
            <button
                ref="carousel-left"
                v-show="showArrowLeft"
                class="l-ss-c-results-carousel-button l-ss-c-results-carousel-button-prev"
                @click="slide('left')">
                <i
                    class="l-ss-c-icon"
                    v-html="iconArrowRight"></i>
            </button>
            <button
                ref="carousel-right"
                v-show="showArrowRight"
                class="l-ss-c-results-carousel-button l-ss-c-results-carousel-button-next"
                @click="slide('right')">
                <i
                    class="l-ss-c-icon"
                    v-html="iconArrowRight"></i>
            </button>
        </template>
    </div>
</template>

<script>
    import Offer from '@/core/components/offer/offer';
    import IconArrowRight from '../assets/images/icons/arrow-right-results.svg';
    import { AVAILABLE_VIEWS } from '@/init/options/OffersOptions';
    import offerHandlersMixin from '@/core/mixins/offer-handlers';
    // import lazyLoadOfferImagesMixin from '@/core/mixins/lazyLoadOfferImagesMixin';

    const SLIDE_SHOW_MORE_SIZE = 152;
    const LONG_TOUCH_DURATION = 500;

    export default {
        components: {
            Offer,
        },
        mixins: [offerHandlersMixin],
        inject: ['loadGroupedOffers'],
        props: {
            offers: {
                type: Array,
                default: () => [],
            },
            store: {
                type: Object,
            },
            searchState: {
                type: Object,
            },
            type: {
                type: String,
                default: 'offers',
            },
            view: {
                type: String,
                default: 'row',
                validate: (value) => AVAILABLE_VIEWS.includes(value),
            },
            offerLayout: {
                type: String,
            },
            offerProps: {
                type: Object,
                default: () => ({}),
            },
            grouped: {
                type: Boolean,
                default: false,
            },
            availableShowMore: {
                type: Boolean,
                default: false,
            },
            adultPermissionConfirmed: {
                type: Boolean,
                default: false,
            },
            breakpoint: {
                type: Object,
            },
        },
        data() {
            return {
                showArrowLeft: false,
                showArrowRight: true,
                wrapperClientWidth: 0,
                handleScroll: () => {
                },
                longTouchTimer: null,
                viewedOffers: [],
            };
        },
        computed: {
            isMobile() {
                return this.$screen.isMobile;
            },
            isGridView() {
                return this.view === 'grid';
            },
            layout() {
                return this.$configuration.layout;
            },
            gridColumnCount() {
                if (this.isMobile) {
                    return 2;
                }

                return this.breakpoint.columnCount;
            },
            gridGap() {
                return this.breakpoint.gridGap;
            },
            gridGapRow() {
                return this.breakpoint.gridGapRow;
            },
            rowGap() {
                return this.layout.rowGap;
            },
            carouselSettings() {
                return this.layout.carousel;
            },
            fakeOffers() {
                if (this.isMobile && this.offers.length % 2 !== 0) {
                    return 1;
                }

                return 0;
            },
            gridStyles() {
                const styles = {};

                if (this.isGridView) {
                    styles.gridGap = `${this.gridGap}px`;
                    styles.rowGap = `${this.gridGapRow}px`;

                    if (!this.grouped || this.isMobile) {
                        styles.gridTemplateColumns = `repeat(${this.gridColumnCount}, minmax(0, 1fr))`;

                        if (this.isMobile) {
                            styles.gridGap = '1px';
                        }
                        return styles;
                    }

                    const slideSize = `calc(${100 / this.gridColumnCount}% - ${this.gridGap - (this.gridGap / this.gridColumnCount)}px)`;

                    if (this.gridColumnCount >= this.offers.length) {
                        const minSize = `${this.wrapperClientWidth / this.gridColumnCount - this.gridGap - (this.gridGap / this.gridColumnCount)}px`;

                        styles.gridTemplateColumns = `repeat(${this.offers.length}, minmax(${minSize}, ${slideSize}))`;
                    } else {
                        let gridTemplateColumns = `repeat(${this.offers.length}, ${slideSize})`;

                        styles.overflow = 'auto';
                        styles.gridTemplateColumns = gridTemplateColumns;
                    }

                    if (this.availableShowMore) {
                        styles.gridTemplateColumns += ` ${SLIDE_SHOW_MORE_SIZE}px`;
                    }
                } else {
                    styles.rowGap = `${this.rowGap}px`;
                }
                return styles;
            },
            iconArrowRight() {
                return this.$configuration.customSvgIconRightArrow ? this.$configuration.customSvgIconRightArrow : IconArrowRight;
            },
            dangerousCategoriesIdList() {
                return this.$configuration.dangerousCategoriesIdList || [];
            },
            adultOffers() {
                return this.offers
                    .filter((offer) => !this.adultPermissionConfirmed && this.dangerousCategoriesIdList.includes(offer.category.id))
                    .map(offer => offer.id);
            },
        },
        watch: {
            offers: {
                immediate: true,
                async handler() {
                    await this.$nextTick();
                    this.initOfferVisibilityObserver();
                },
            },
        },
        mounted() {
            this.wrapperClientWidth = this.$refs.wrapper.clientWidth;

            this.$nextTick(() => {
                this.scrollToOffer();
            });
        },
        methods: {
            initOfferVisibilityObserver() {
                const observer = new IntersectionObserver((entries) => {
                    this.viewedOffers = JSON.parse(sessionStorage.getItem('viewedOffers')) || [];

                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const offerId = entry.target.dataset.offerId;

                            if (!this.viewedOffers.includes(offerId)) {
                                this.viewedOffers.push(offerId);
                                sessionStorage.setItem('viewedOffers', JSON.stringify(this.viewedOffers));
                            }

                            observer.unobserve(entry.target);
                        }
                    });
                }, {
                    rootMargin: '0px',
                    threshold: 1.0,
                });

                const offerComponents = this.$refs.offer || [];
                this.viewedOffers = JSON.parse(sessionStorage.getItem('viewedOffers')) || [];

                for (const offer of offerComponents) {
                    if (!this.viewedOffers.includes(offer.$el.dataset.offerId)) {
                        observer.observe(offer.$el);
                    }
                }
            },
            scrollToOffer() {
                const idOffer = this.searchState?.public.idOffer;
                if (idOffer && this.store?.state.availableScrollToOffer) {
                    const element = this.$refs.wrapper.querySelector(`[data-offer-id="${idOffer}"]`);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        this.store.commit('availableScrollToOffer/SET_PROPERTY', false);
                    }
                }
            },
            onTouchStart(event) {
                const customComposedPath = event.composedPath && event.composedPath();
                this.longTouchTimer = setTimeout(() => this.onLongTouch(event, customComposedPath), LONG_TOUCH_DURATION);
            },
            onTouchEnd() {
                if (this.longTouchTimer) {
                    clearTimeout(this.longTouchTimer);
                }
            },
            onLongTouch(event, customComposedPath) {
                this.globalHandleOfferClick(event, { customComposedPath });
            },
            scrollToSlide(wrapper, block, duration) {
                const OFFSET_LEFT = 0;
                const OFFSET_RIGHT = 0;

                const startScroll = wrapper.scrollLeft;
                const blockStart = block.offsetLeft - OFFSET_LEFT;
                const blockEnd = blockStart + block.offsetWidth + OFFSET_RIGHT;

                const isScrollingRight = blockStart > startScroll;

                const endScroll = isScrollingRight ?
                    blockEnd - wrapper.clientWidth :
                    blockStart;

                const scrollDistance = endScroll - startScroll;
                const startTime = performance.now();

                function animateScroll(currentTime) {
                    const timeElapsed = currentTime - startTime;
                    const progress = Math.min(timeElapsed / duration, 1);

                    // Easing function (easeInOutQuad)
                    // eslint-disable-next-line no-magic-numbers
                    const easing = progress < 0.5
                        ? 2 * progress * progress
                        // eslint-disable-next-line no-magic-numbers
                        : -1 + (4 - 2 * progress) * progress;

                    wrapper.scrollLeft = startScroll + scrollDistance * easing;

                    if (timeElapsed < duration) {
                        requestAnimationFrame(animateScroll);
                    } else {
                        wrapper.scrollLeft = endScroll; // Ensure it ends at the exact position
                    }
                }

                requestAnimationFrame(animateScroll);
            },
            slide(direction = 'right') {
                const slideWidth = this.$refs.offer[0].$el.offsetWidth;
                const slideCount = this.$refs.offer.length;
                const wrapperScrollLeft = this.$refs.wrapper.scrollLeft;
                const scrolledSlides = Math.round(wrapperScrollLeft / (slideWidth + this.gridGap));

                const { scrollSlideCount, scrollSpeed } = this.carouselSettings;

                let scrollToEl = null;
                let index = 0;

                if (direction === 'right') {
                    index = scrolledSlides + this.gridColumnCount + scrollSlideCount - 1;
                    // Проверяем, не вышли ли мы за пределы списка слайдов
                    if (index >= slideCount) {
                        /*
                         * Возможно нужно сделать, чтобы скорость слайдов зависила от количества слайдов, которое получится прокрутить
                         * Const newScrollSlideCount = scrollSlideCount - index + slideCount - 1;
                         * newScrollSpeed = scrollSpeed * newScrollSlideCount / scrollSlideCount;
                         */

                        if (this.$slots.append) {
                            const appendSlotSelector = this.$slots.append[0].data.class;
                            scrollToEl = this.$refs.wrapper.getElementsByClassName(appendSlotSelector)[0];
                        } else {
                            index = slideCount - 1;
                        }
                    }
                } else {
                    index = scrolledSlides - scrollSlideCount;
                    // Проверяем, не вышли ли мы за пределы списка слайдов
                    index = index >= 0 ? index : 0;
                }

                /*
                 * Если scrollToEl не был установлен (то есть мы не вышли за пределы списка слайдов),
                 * используем текущий слайд по индексу.
                 */
                if (!scrollToEl) {
                    scrollToEl = this.$refs.offer[index].$el;
                }

                this.scrollToSlide(this.$refs.wrapper, scrollToEl, scrollSpeed);
            },
            processAvailableArrows() {
                const { wrapper } = this.$refs;

                const wrapperScrollLeft = wrapper.scrollLeft;
                const wrapperScrollWidth = wrapper.scrollWidth;
                const wrapperWidth = wrapper.clientWidth;

                const OFFSET_LEFT = 50;
                const OFFSET_RIGHT = 100;

                this.showArrowLeft = wrapperScrollLeft > OFFSET_LEFT;
                this.showArrowRight = wrapperScrollLeft + wrapperWidth - wrapperScrollWidth <= -OFFSET_RIGHT;
            },
            getOfferPosition(index, options = {}) {
                const { columnCount } = this.breakpoint;

                const OFFERS_PER_ROW_MOBILE = 2;
                const OFFERS_PER_ROW_CUT = columnCount - 1;
                const OFFERS_PER_ROW_DEFAULT = options.groupIndex !== undefined ? OFFERS_PER_ROW_CUT : columnCount;
                const ADAPTIVE_OFFERS_PER_ROW = this.$screen.isMobile ? OFFERS_PER_ROW_MOBILE : OFFERS_PER_ROW_DEFAULT;
                let pos = index + 1;
                let row = Math.floor(index / ADAPTIVE_OFFERS_PER_ROW) + 1;
                let page = isNaN(parseInt(options.page)) ? 1 : parseInt(options.page) || 1;
                return {
                    pos: (options.startIndex ? index + options.startIndex + 1 : pos) * page,
                    row: options.row ? parseInt(options.row) : row,
                    col: this.view === 'row' ? index + 1 : ADAPTIVE_OFFERS_PER_ROW -
                        ((row * ADAPTIVE_OFFERS_PER_ROW) - pos) +
                        (ADAPTIVE_OFFERS_PER_ROW < OFFERS_PER_ROW_DEFAULT ? (options.groupIndex || 0) : 0),
                };
            },
            handleOfferClick(event, offer) {
                if (this.adultOffers.includes(offer.id)) {
                    event.preventDefault();

                    this.$emit('click-adult', offer);
                }
            },
        },
    };
</script>
